import React, { useState, useEffect, useCallback } from 'react';
import {
  getAdminOverview, getAdminTables, getAdminDictionary,
  getAdminLineage, getAdminRelationships, getAdminOnelake
} from '../api';
import {
  ArrowLeft, SpinnerGap, ChartLineUp, Table, BookOpen, TreeStructure,
  GitBranch, CloudArrowUp, CheckCircle, Clock, ArrowRight,
  Plugs
} from '@phosphor-icons/react';

const TABS = [
  { id: 'overview', label: 'Overview', icon: ChartLineUp },
  { id: 'tables', label: 'Data Tables', icon: Table },
  { id: 'dictionary', label: 'Data Dictionary', icon: BookOpen },
  { id: 'lineage', label: 'Data Lineage', icon: TreeStructure },
  { id: 'relationships', label: 'Relationships', icon: GitBranch },
  { id: 'onelake', label: 'OneLake Connection', icon: CloudArrowUp },
];

export default function AdminDataManagement({ onBack }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [tables, setTables] = useState(null);
  const [dictionary, setDictionary] = useState(null);
  const [lineage, setLineage] = useState(null);
  const [relationships, setRelationships] = useState(null);
  const [onelake, setOnelake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dictFilter, setDictFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, tbRes, diRes, liRes, reRes, olRes] = await Promise.all([
        getAdminOverview(), getAdminTables(), getAdminDictionary(),
        getAdminLineage(), getAdminRelationships(), getAdminOnelake(),
      ]);
      setOverview(ovRes.data);
      setTables(tbRes.data);
      setDictionary(diRes.data);
      setLineage(liRes.data);
      setRelationships(reRes.data);
      setOnelake(olRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center h-64"><SpinnerGap size={32} className="text-primary animate-spin" /></div>
  );

  return (
    <div className="space-y-5" data-testid="admin-data-management">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-background border border-border text-text-secondary hover:text-text-primary transition-all" data-testid="management-back-btn">
          <ArrowLeft size={18} weight="bold" />
        </button>
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-extrabold text-text-primary">Data Management Console</h1>
          <p className="font-body text-xs text-text-secondary mt-0.5">Semantic model, data dictionary, lineage, and OneLake connection management.</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 bg-surface border border-border rounded-xl p-1 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`dm-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg font-heading text-xs font-semibold transition-all whitespace-nowrap ${activeTab === t.id ? 'bg-primary text-white' : 'text-text-secondary hover:bg-background hover:text-text-primary'}`}>
              <Icon size={14} weight={activeTab === t.id ? 'fill' : 'duotone'} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && overview && <OverviewTab data={overview} />}
      {activeTab === 'tables' && tables && <TablesTab data={tables} />}
      {activeTab === 'dictionary' && dictionary && <DictionaryTab data={dictionary} filter={dictFilter} setFilter={setDictFilter} />}
      {activeTab === 'lineage' && lineage && <LineageTab data={lineage} />}
      {activeTab === 'relationships' && relationships && <RelationshipsTab data={relationships} />}
      {activeTab === 'onelake' && onelake && <OnelakeTab data={onelake} />}
    </div>
  );
}

function OverviewTab({ data }) {
  const stats = [
    { label: 'Collections', value: data.total_collections, color: 'text-text-primary' },
    { label: 'Total Records', value: data.total_records.toLocaleString(), color: 'text-primary' },
    { label: 'Pipeline Connections', value: data.pipeline_connections, color: 'text-text-primary' },
    { label: 'Healthy Pipelines', value: data.pipeline_healthy, color: 'text-emerald-600' },
    { label: 'Degraded', value: data.pipeline_degraded, color: data.pipeline_degraded > 0 ? 'text-amber-600' : 'text-emerald-600' },
    { label: 'Data Models', value: data.data_models, color: 'text-text-primary' },
    { label: 'ML Models Active', value: `${data.ml_models_active}/${data.ml_models_total}`, color: 'text-text-primary' },
    { label: 'OneLake Shortcuts', value: data.onelake_shortcuts, color: 'text-text-primary' },
  ];

  return (
    <div className="space-y-5" data-testid="dm-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <div key={i} className="bg-surface border border-border rounded-xl p-4 text-center">
            <p className={`font-heading text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>
      {/* Collection breakdown */}
      <div className="bg-surface border border-border rounded-xl p-5">
        <h3 className="font-heading text-sm font-bold text-text-primary mb-3">Collection Record Counts</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {Object.entries(data.collection_stats).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
            <div key={name} className="flex items-center justify-between p-2.5 bg-background rounded-lg border border-border/50">
              <span className="font-body text-xs text-text-primary font-mono truncate">{name}</span>
              <span className="font-heading text-xs font-bold text-primary ml-2">{count}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-surface border border-border rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle size={16} weight="fill" className="text-emerald-600" />
          <span className="font-body text-sm text-text-primary">OneLake Status: <span className="font-semibold text-emerald-600">{data.onelake_status}</span></span>
        </div>
        <span className="font-body text-xs text-text-secondary">Last sync: {new Date(data.last_sync).toLocaleString()}</span>
      </div>
    </div>
  );
}

function TablesTab({ data }) {
  return (
    <div className="space-y-2" data-testid="dm-tables">
      {data.tables.map(tbl => (
        <div key={tbl.name} className="bg-surface border border-border rounded-xl p-4 hover:border-primary/30 transition-all">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Table size={14} className="text-primary" />
              <span className="font-heading text-sm font-bold text-text-primary font-mono">{tbl.name}</span>
            </div>
            <div className="flex items-center gap-3 text-[10px] font-body text-text-secondary">
              <span>{tbl.record_count.toLocaleString()} rows</span>
              <span>{tbl.field_count} fields</span>
            </div>
          </div>
          <p className="font-body text-xs text-text-secondary mt-1">{tbl.description}</p>
          {tbl.used_by && tbl.used_by.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {tbl.used_by.map((v, i) => (
                <span key={i} className="px-2 py-0.5 bg-primary/10 text-primary rounded text-[10px] font-body font-medium">{v}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function DictionaryTab({ data, filter, setFilter }) {
  const tables = Object.keys(data.dictionary);
  const filtered = filter ? tables.filter(t => t.includes(filter.toLowerCase())) : tables;

  return (
    <div className="space-y-4" data-testid="dm-dictionary">
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter tables..."
        className="w-full max-w-sm px-4 py-2 bg-surface border border-border rounded-lg font-body text-sm text-text-primary placeholder:text-tertiary focus:outline-none focus:border-primary"
      />
      {filtered.map(tableName => (
        <div key={tableName} className="bg-surface border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-background/50 border-b border-border">
            <span className="font-heading text-sm font-bold text-text-primary font-mono">{tableName}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] font-body">
              <thead>
                <tr className="border-b border-border bg-background/30">
                  <th className="text-left py-2 px-4 text-tertiary font-semibold uppercase tracking-wider">Field</th>
                  <th className="text-left py-2 px-4 text-tertiary font-semibold uppercase tracking-wider">Type</th>
                  <th className="text-left py-2 px-4 text-tertiary font-semibold uppercase tracking-wider">Description</th>
                  <th className="text-left py-2 px-4 text-tertiary font-semibold uppercase tracking-wider">Nullable</th>
                  <th className="text-left py-2 px-4 text-tertiary font-semibold uppercase tracking-wider">Example</th>
                </tr>
              </thead>
              <tbody>
                {data.dictionary[tableName].map((f, i) => (
                  <tr key={i} className="border-b border-border/30 hover:bg-background/30">
                    <td className="py-2 px-4 text-text-primary font-medium font-mono">{f.field}</td>
                    <td className="py-2 px-4 text-amber-600 font-mono">{f.type}</td>
                    <td className="py-2 px-4 text-text-secondary">{f.description}</td>
                    <td className="py-2 px-4">{f.nullable ? <span className="text-tertiary">Yes</span> : <span className="text-emerald-600 font-semibold">No</span>}</td>
                    <td className="py-2 px-4 text-text-secondary font-mono text-[10px]">{f.example}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function LineageTab({ data }) {
  const STATUS_STYLE = {
    'Active': 'bg-emerald-500/15 text-emerald-700',
    'Degraded': 'bg-amber-500/15 text-amber-700',
  };

  return (
    <div className="space-y-3" data-testid="dm-lineage">
      {data.lineage.map((l) => (
        <div key={l.id} className="bg-surface border border-border rounded-xl p-4 hover:border-primary/30 transition-all">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Source */}
            <div className="px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-[10px] font-body text-blue-600 uppercase tracking-wider font-semibold">{l.source_type}</p>
              <p className="font-heading text-xs font-bold text-text-primary mt-0.5">{l.source}</p>
            </div>
            <ArrowRight size={14} className="text-tertiary flex-shrink-0" />
            {/* Processing */}
            <div className="px-3 py-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <p className="text-[10px] font-body text-purple-600 uppercase tracking-wider font-semibold">Processing</p>
              <p className="font-heading text-xs font-bold text-text-primary mt-0.5">{l.processing}</p>
            </div>
            <ArrowRight size={14} className="text-tertiary flex-shrink-0" />
            {/* Target */}
            <div className="px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <p className="text-[10px] font-body text-emerald-600 uppercase tracking-wider font-semibold">Collection</p>
              <p className="font-heading text-xs font-bold text-text-primary mt-0.5 font-mono">{l.target_table}</p>
            </div>
            <ArrowRight size={14} className="text-tertiary flex-shrink-0" />
            {/* Output Views */}
            <div className="flex flex-wrap gap-1">
              {l.output_views.map((v, i) => (
                <span key={i} className="px-2 py-1 bg-primary/10 text-primary rounded text-[10px] font-body font-medium">{v}</span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4 mt-3 pt-2 border-t border-border/50 text-[10px] font-body text-text-secondary">
            <div className="flex items-center gap-1">
              <Clock size={10} /> {l.refresh}
            </div>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${STATUS_STYLE[l.status] || ''}`}>{l.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function RelationshipsTab({ data }) {
  return (
    <div data-testid="dm-relationships">
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-body">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="text-left py-3 px-4 text-tertiary font-semibold uppercase tracking-wider">From Table</th>
                <th className="text-left py-3 px-4 text-tertiary font-semibold uppercase tracking-wider">Field</th>
                <th className="text-left py-3 px-4 text-tertiary font-semibold uppercase tracking-wider"></th>
                <th className="text-left py-3 px-4 text-tertiary font-semibold uppercase tracking-wider">To Table</th>
                <th className="text-left py-3 px-4 text-tertiary font-semibold uppercase tracking-wider">Field</th>
                <th className="text-left py-3 px-4 text-tertiary font-semibold uppercase tracking-wider">Cardinality</th>
                <th className="text-left py-3 px-4 text-tertiary font-semibold uppercase tracking-wider">Description</th>
              </tr>
            </thead>
            <tbody>
              {data.relationships.map((r, i) => (
                <tr key={i} className="border-b border-border/30 hover:bg-background/30">
                  <td className="py-2.5 px-4 text-text-primary font-medium font-mono">{r.from_table}</td>
                  <td className="py-2.5 px-4 text-amber-600 font-mono">{r.from_field}</td>
                  <td className="py-2.5 px-2 text-center">
                    <ArrowRight size={12} className="text-primary inline" />
                  </td>
                  <td className="py-2.5 px-4 text-text-primary font-medium font-mono">{r.to_table}</td>
                  <td className="py-2.5 px-4 text-amber-600 font-mono">{r.to_field}</td>
                  <td className="py-2.5 px-4">
                    <span className="px-2 py-0.5 bg-blue-500/10 text-blue-700 rounded text-[10px] font-body font-semibold">{r.cardinality}</span>
                  </td>
                  <td className="py-2.5 px-4 text-text-secondary">{r.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function OnelakeTab({ data }) {
  return (
    <div className="space-y-5" data-testid="dm-onelake">
      {/* Connection Config */}
      <div className="bg-surface border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Plugs size={16} className="text-primary" />
          <h3 className="font-heading text-sm font-bold text-text-primary">Connection Configuration</h3>
          <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${data.status === 'Connected' ? 'bg-emerald-500/15 text-emerald-700' : 'bg-red-500/15 text-red-700'}`}>
            {data.status}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: 'Workspace', value: data.workspace },
            { label: 'Lakehouse', value: data.lakehouse },
            { label: 'Region', value: data.region },
            { label: 'Auth Method', value: data.auth_method },
            { label: 'Tenant ID', value: data.tenant_id },
            { label: 'Client ID', value: data.client_id },
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-background rounded-lg border border-border/50">
              <span className="text-[11px] font-body text-text-secondary">{item.label}</span>
              <span className="text-[11px] font-body font-semibold text-text-primary font-mono">{item.value}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 p-3 bg-background rounded-lg border border-border/50">
          <span className="text-[11px] font-body text-text-secondary">Endpoint</span>
          <p className="text-[11px] font-body font-mono text-primary mt-0.5 break-all">{data.endpoint}</p>
        </div>
      </div>

      {/* Sync Schedule */}
      <div className="bg-surface border border-border rounded-xl p-5">
        <h3 className="font-heading text-sm font-bold text-text-primary mb-3">Sync Schedule</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Frequency', value: data.sync_schedule.frequency },
            { label: 'Last Successful', value: new Date(data.sync_schedule.last_successful).toLocaleString() },
            { label: 'Next Scheduled', value: new Date(data.sync_schedule.next_scheduled).toLocaleString() },
            { label: 'Failures (24h)', value: data.sync_schedule.failures_last_24h, color: data.sync_schedule.failures_last_24h > 0 ? 'text-red-600' : 'text-emerald-600' },
          ].map((s, i) => (
            <div key={i} className="p-3 bg-background rounded-lg border border-border/50 text-center">
              <p className={`font-heading text-sm font-bold ${s.color || 'text-text-primary'}`}>{s.value}</p>
              <p className="text-[10px] font-body text-text-secondary mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Shortcuts */}
      <div className="bg-surface border border-border rounded-xl p-5">
        <h3 className="font-heading text-sm font-bold text-text-primary mb-3">OneLake Shortcuts</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-body">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 text-tertiary font-semibold uppercase tracking-wider">Shortcut</th>
                <th className="text-left py-2 px-3 text-tertiary font-semibold uppercase tracking-wider">Source Path</th>
                <th className="text-left py-2 px-3 text-tertiary font-semibold uppercase tracking-wider">Target</th>
                <th className="text-left py-2 px-3 text-tertiary font-semibold uppercase tracking-wider">Format</th>
                <th className="text-left py-2 px-3 text-tertiary font-semibold uppercase tracking-wider">Rows Synced</th>
                <th className="text-left py-2 px-3 text-tertiary font-semibold uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.shortcuts.map((sc, i) => (
                <tr key={i} className="border-b border-border/30 hover:bg-background/30">
                  <td className="py-2 px-3 text-text-primary font-medium font-mono">{sc.name}</td>
                  <td className="py-2 px-3 text-text-secondary font-mono">{sc.source_path}</td>
                  <td className="py-2 px-3 text-primary font-mono">{sc.target_collection}</td>
                  <td className="py-2 px-3"><span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-700 rounded text-[9px] font-semibold">{sc.format}</span></td>
                  <td className="py-2 px-3 text-text-primary font-semibold">{sc.rows_synced.toLocaleString()}</td>
                  <td className="py-2 px-3">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${sc.status === 'Active' ? 'bg-emerald-500/15 text-emerald-700' : 'bg-amber-500/15 text-amber-700'}`}>
                      {sc.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
